import React, { useEffect, useMemo, useState } from 'react';

const formatDateTime = (value) => {
    if (!value) {
        return '—';
    }
    try {
        return new Date(value).toLocaleString();
    } catch (error) {
        return value;
    }
};

const getHtmlFromBackup = (backup) => {
    if (!backup) {
        return '';
    }
    if (backup.html) {
        return backup.html;
    }
    if (backup.properties) {
        if (backup.properties['cb_html']) {
            return backup.properties['cb_html'];
        }
        if (backup.properties['gjs-html']) {
            return backup.properties['gjs-html'];
        }
    }
    return '';
};

const BackupManager = ({
    entityLabel = 'item',
    fetchBackups,
    fetchBackupDetail,
    restoreBackup,
    onRestoreSuccess,
}) => {
    const [backups, setBackups] = useState([]);
    const [selectedBackup, setSelectedBackup] = useState(null);
    const [loadingList, setLoadingList] = useState(true);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [restoringId, setRestoringId] = useState(null);
    const [alert, setAlert] = useState(null);

    const loadBackups = async () => {
        setLoadingList(true);
        try {
            const items = await fetchBackups();
            setBackups(items);
            if (!selectedBackup && items.length > 0) {
                handlePreview(items[0].id);
            } else if (
                selectedBackup &&
                !items.find((backup) => backup.id === selectedBackup.id)
            ) {
                setSelectedBackup(null);
            }
        } catch (error) {
            setAlert({ type: 'error', text: 'Failed to load backups.' });
        } finally {
            setLoadingList(false);
        }
    };

    useEffect(() => {
        loadBackups();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handlePreview = async (backupId) => {
        setPreviewLoading(true);
        try {
            const backup = await fetchBackupDetail(backupId);
            setSelectedBackup(backup);
        } catch (error) {
            setAlert({ type: 'error', text: 'Unable to load that backup.' });
        } finally {
            setPreviewLoading(false);
        }
    };

    const handleRestore = async (backupId) => {
        const confirmed = window.confirm(
            `Restoring this backup will overwrite the current ${entityLabel} content. Continue?`
        );
        if (!confirmed) {
            return;
        }

        setRestoringId(backupId);
        try {
            await restoreBackup(backupId);
            setAlert({
                type: 'success',
                text: `Backup restored. The ${entityLabel} now matches the selected snapshot.`,
            });
            if (onRestoreSuccess) {
                onRestoreSuccess();
            }
            loadBackups();
        } catch (error) {
            setAlert({ type: 'error', text: 'Failed to restore backup.' });
        } finally {
            setRestoringId(null);
        }
    };

    const htmlContent = getHtmlFromBackup(selectedBackup);
    const stylesContent = selectedBackup?.styles || selectedBackup?.properties?.styles || '';
    const previewDoc = useMemo(() => {
        if (!htmlContent) {
            return '';
        }
        return `<!DOCTYPE html><html><head><meta charset="UTF-8" />` +
            `<style>${stylesContent || ''}</style></head><body>${htmlContent}</body></html>`;
    }, [htmlContent, stylesContent]);

    return (
        <div className="space-y-6">
            {alert && (
                <div
                    className={`rounded-md border px-4 py-3 text-sm ${
                        alert.type === 'success'
                            ? 'border-green-200 bg-green-50 text-green-800'
                            : 'border-red-200 bg-red-50 text-red-800'
                    }`}
                >
                    {alert.text}
                </div>
            )}

            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
                    <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
                        <div>
                            <h3 className="text-lg font-semibold text-gray-900">Available Backups</h3>
                            <p className="text-sm text-gray-500">Preview or restore any saved snapshot.</p>
                        </div>
                        <button
                            type="button"
                            onClick={loadBackups}
                            className="text-sm font-medium text-indigo-600 hover:text-indigo-900"
                        >
                            Refresh
                        </button>
                    </div>
                    <div className="divide-y divide-gray-100">
                        {loadingList && (
                            <div className="p-4 text-sm text-gray-500">Loading backups...</div>
                        )}
                        {!loadingList && backups.length === 0 && (
                            <div className="p-6 text-sm text-center text-gray-500">
                                No backups found yet.
                            </div>
                        )}
                        {!loadingList &&
                            backups.length > 0 &&
                            backups.map((backup) => (
                                <div key={backup.id} className="flex items-start justify-between gap-4 px-4 py-3">
                                    <div>
                                        <p className="text-sm font-medium text-gray-900">
                                            {backup.title || `Backup #${backup.id}`}
                                        </p>
                                        <p className="text-xs text-gray-500">
                                            {formatDateTime(backup.created_at)}
                                        </p>
                                        {backup.owner_name && (
                                            <p className="text-xs text-gray-500">Owner: {backup.owner_name}</p>
                                        )}
                                        {backup.tags && (
                                            <p className="text-xs text-gray-500">Tags: {backup.tags}</p>
                                        )}
                                    </div>
                                    <div className="flex flex-col items-end space-y-2 text-right">
                                        <button
                                            type="button"
                                            onClick={() => handlePreview(backup.id)}
                                            className="text-sm font-medium text-indigo-600 hover:text-indigo-900"
                                        >
                                            Preview
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => handleRestore(backup.id)}
                                            className="text-sm font-medium text-red-600 hover:text-red-800 disabled:opacity-50"
                                            disabled={restoringId === backup.id}
                                        >
                                            {restoringId === backup.id ? 'Restoring…' : 'Restore'}
                                        </button>
                                    </div>
                                </div>
                            ))}
                    </div>
                </div>

                <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
                    <div className="flex items-start justify-between px-4 py-3 border-b border-gray-200">
                        <div>
                            <h3 className="text-lg font-semibold text-gray-900">Preview</h3>
                            <p className="text-sm text-gray-500">
                                {selectedBackup
                                    ? `Snapshot captured ${formatDateTime(selectedBackup.created_at)}`
                                    : 'Select a backup to preview its content.'}
                            </p>
                        </div>
                        {selectedBackup && (
                            <button
                                type="button"
                                onClick={() => handleRestore(selectedBackup.id)}
                                className="inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 disabled:opacity-50"
                                disabled={restoringId === selectedBackup.id}
                            >
                                {restoringId === selectedBackup.id ? 'Restoring…' : 'Restore backup'}
                            </button>
                        )}
                    </div>
                    <div className="p-4 space-y-4">
                        {previewLoading && (
                            <div className="text-sm text-gray-500">Loading preview…</div>
                        )}
                        {!previewLoading && !selectedBackup && (
                            <div className="text-sm text-gray-500">Choose a backup to inspect its content.</div>
                        )}
                        {!previewLoading && selectedBackup && (
                            <>
                                <div className="grid grid-cols-2 gap-4 text-sm">
                                    <div>
                                        <p className="text-gray-500">Title</p>
                                        <p className="font-medium text-gray-900">{selectedBackup.title || 'Untitled backup'}</p>
                                    </div>
                                    <div>
                                        <p className="text-gray-500">Owner</p>
                                        <p className="font-medium text-gray-900">
                                            {selectedBackup.owner?.name || 'Unknown'}
                                        </p>
                                    </div>
                                    <div>
                                        <p className="text-gray-500">Tags</p>
                                        <p className="font-medium text-gray-900">{selectedBackup.tags || '—'}</p>
                                    </div>
                                    <div>
                                        <p className="text-gray-500">Created</p>
                                        <p className="font-medium text-gray-900">{formatDateTime(selectedBackup.created_at)}</p>
                                    </div>
                                </div>
                                <div>
                                    <p className="mb-2 text-sm font-medium text-gray-700">Rendered Preview</p>
                                    {previewDoc ? (
                                        // Use sandboxed iframe to keep backup markup isolated from the editor shell.
                                        <iframe
                                            title="backup-preview"
                                            className="w-full border border-gray-200 rounded-md h-96"
                                            sandbox=""
                                            srcDoc={previewDoc}
                                        />
                                    ) : (
                                        <div className="p-4 text-sm text-gray-500 border border-gray-300 border-dashed rounded-md">
                                            This backup does not contain HTML content.
                                        </div>
                                    )}
                                </div>
                                {htmlContent && (
                                    <div>
                                        <p className="mb-2 text-sm font-medium text-gray-700">Raw HTML</p>
                                        <pre className="p-3 overflow-auto text-xs text-green-100 whitespace-pre-wrap bg-gray-900 rounded-md max-h-48">{htmlContent}</pre>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BackupManager;
